import { MaskitoOptions } from '@maskito/core';

const LETTER = /[A-Za-z]/;
const DIGIT = /\d/;
const LETTER_OR_DIGIT = /[A-Za-z\d]/;

/** Character expected at each position of a Canadian postal code, ignoring the space (A1A 1A1). */
const CANADA_POSTAL_CODE_SLOTS = [LETTER, DIGIT, LETTER, DIGIT, LETTER, DIGIT];

function postalCodeCharacters(value: string): string {
  return value
    .split('')
    .filter(character => LETTER_OR_DIGIT.test(character))
    .join('');
}

function fitsPostalCodeSlots(characters: string): boolean {
  return characters.split('').every((character, index) => CANADA_POSTAL_CODE_SLOTS[index]?.test(character));
}

/**
 * The mask itself accepts a letter or digit in every position, so deleting from the middle removes only that
 * character and lets the rest shift left. Typed and pasted characters are still held to the A1A 1A1 pattern here.
 */
export const CANADA_POSTAL_CODE_MASK_OPTIONS: MaskitoOptions = {
  mask: [LETTER_OR_DIGIT, LETTER_OR_DIGIT, LETTER_OR_DIGIT, ' ', LETTER_OR_DIGIT, LETTER_OR_DIGIT, LETTER_OR_DIGIT],
  preprocessors: [
    ({ elementState, data }, actionType) => {
      if (actionType !== 'insert') {
        return { elementState, data };
      }

      const { value, selection } = elementState;
      const [from, to] = selection;
      const charactersBefore = postalCodeCharacters(value.slice(0, from));
      const charactersAfter = postalCodeCharacters(value.slice(to));
      let slotIndex = charactersBefore.length;
      let acceptedData = '';

      for (const character of data) {
        if (slotIndex + charactersAfter.length >= CANADA_POSTAL_CODE_SLOTS.length) {
          break;
        }
        if (CANADA_POSTAL_CODE_SLOTS[slotIndex].test(character)) {
          acceptedData += character;
          slotIndex++;
        }
      }

      // A code already out of pattern after a deletion is exempt, so the missing character can be typed back in.
      const insertBreaksPattern =
        fitsPostalCodeSlots(charactersBefore + charactersAfter) &&
        !fitsPostalCodeSlots(charactersBefore + acceptedData + charactersAfter);
      if (insertBreaksPattern) {
        return { elementState, data: '' };
      }

      return { elementState, data: acceptedData };
    }
  ],
  postprocessors: [({ value, selection }) => ({ value: value.toUpperCase(), selection })]
};
